import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, Check, Star, Play, Brain, Puzzle } from "lucide-react";
import { TOPIC_MAP, LEVELS } from "@/lib/topics";
import { useClient } from "@/context/ClientContext";

export default function LevelMap() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { progress, clientId } = useClient();
  const topic = TOPIC_MAP[topicId];

  if (!topic) {
    navigate(`/clients/${clientId}`);
    return null;
  }

  const start = (difficulty, mode) => navigate(`/clients/${clientId}/play/${topicId}/${difficulty}/${mode}`);

  return (
    <div className="relative overflow-hidden min-h-[calc(100vh-4rem)]">
      <div
        className="pointer-events-none absolute -top-20 right-0 w-96 h-96 rounded-full blur-3xl floaty"
        style={{ background: `${topic.color}22` }}
      />
      <div className="relative max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-24">
        <button
          data-testid="back-btn"
          onClick={() => navigate(`/clients/${clientId}`)}
          className="inline-flex items-center gap-1.5 text-slate-400 hover:text-slate-700 font-bold text-sm mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> All learnings
        </button>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <span className="text-xs font-bold uppercase tracking-[0.15em]" style={{ color: topic.color }}>
            {topic.tagline}
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#005580] mt-1">{topic.name}</h1>
        </motion.div>

        <div className="relative">
          {/* connecting line */}
          <div className="absolute left-[27px] top-6 bottom-6 w-1 bg-slate-100 rounded-full" />

          <div className="space-y-5">
            {LEVELS.map((lvl, i) => {
              const cell = progress[topicId][lvl.id];
              const locked = !cell.unlocked;
              return (
                <motion.div
                  key={lvl.id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1, type: "spring", stiffness: 240, damping: 22 }}
                  className="relative flex gap-4 items-start"
                >
                  {/* node */}
                  <div
                    data-testid={`level-node-${lvl.id}`}
                    className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center border-b-4 shrink-0"
                    style={{
                      background: locked ? "#E2E8F0" : cell.completed ? "#34D399" : topic.color,
                      borderColor: locked ? "#CBD5E1" : cell.completed ? "#059669" : topic.deep,
                    }}
                  >
                    {locked ? (
                      <Lock className="w-6 h-6 text-slate-400" strokeWidth={2.5} />
                    ) : cell.completed ? (
                      <Check className="w-7 h-7 text-white" strokeWidth={3} />
                    ) : (
                      <span className="font-display font-bold text-white text-xl">{i + 1}</span>
                    )}
                  </div>

                  {/* card */}
                  <div
                    className={`flex-1 rounded-3xl border-2 p-5 bg-white shadow-sm ${
                      locked ? "opacity-60" : ""
                    }`}
                    style={{ borderColor: locked ? "#E2E8F0" : topic.tint }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{lvl.label}</span>
                        <h3 className="font-display font-bold text-lg text-slate-800 leading-tight">{lvl.name}</h3>
                      </div>
                      <Stars count={cell.stars} />
                    </div>

                    {locked ? (
                      <p className="text-sm text-slate-400 font-semibold mt-2">
                        Clear the previous level to unlock.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-3">
                        <button
                          data-testid={`play-quiz-${lvl.id}`}
                          onClick={() => start(lvl.id, "quiz")}
                          className="inline-flex items-center gap-1.5 text-white font-bold text-sm rounded-2xl px-4 py-2 border-b-4 active:border-b-0 active:translate-y-[3px] transition-all"
                          style={{ background: topic.color, borderColor: topic.deep }}
                        >
                          <Brain className="w-4 h-4" strokeWidth={2.5} /> Quiz
                        </button>
                        <button
                          data-testid={`play-scenario-${lvl.id}`}
                          onClick={() => start(lvl.id, "scenario")}
                          className="inline-flex items-center gap-1.5 text-slate-700 font-bold text-sm rounded-2xl px-4 py-2 border-2 border-b-4 border-slate-200 active:border-b-2 active:translate-y-[2px] transition-all bg-white"
                        >
                          <Puzzle className="w-4 h-4" strokeWidth={2.5} /> Scenario
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

const Stars = ({ count }) => (
  <div className="flex gap-0.5" data-testid="level-stars">
    {[0, 1, 2].map((i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < count ? "fill-amber-400 text-amber-400" : "fill-slate-100 text-slate-200"}`}
      />
    ))}
  </div>
);
